import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { ApiKey, ClientOut, ClientRef, Hour, KeyActivity, PageClientOut, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { addDays, civilOf } from "../../lib/civil.ts";
import { describeClient } from "../../lib/clients.ts";
import { instantToLocal, todayIn } from "../../lib/datetime.ts";
import { formatDate, formatInstant, formatRelative } from "../../lib/format.ts";
import { orgApi } from "../../lib/paths.ts";
import { useAction, useResource } from "../../lib/resource.ts";
import { timeZone } from "../../lib/zone.ts";
import { Button } from "../../ui/Button.tsx";
import { Dialog, DialogActions } from "../../ui/Dialog.tsx";
import { Async, Badge, EmptyState } from "../../ui/misc.tsx";
import { Table, Td, Th } from "../../ui/Table.tsx";

const DAYS = 30;
const MIN_BAR = 2;
const FULL = 100;

interface Day {
  date: string;
  requests: number;
  writes: number;
  errors: number;
}

/** The last `DAYS` days in the viewer's zone (oldest first), with the hourly counts summed into them. */
function days(hours: readonly Hour[]): Day[] {
  const today = todayIn(timeZone.value);
  const out: Day[] = [];
  for (let i = DAYS - 1; i >= 0; i -= 1) {
    out.push({ date: addDays(today, -i), requests: 0, writes: 0, errors: 0 });
  }
  for (const h of hours) {
    const d = out.find((x) => x.date === civilOf(instantToLocal(h.hour, timeZone.value)));
    if (d) {
      d.requests += h.requests;
      d.writes += h.writes;
      d.errors += h.errors;
    }
  }
  return out;
}

/** Requests per day as bars (changes and errors in the tooltip). */
function Chart({ hours }: { hours: readonly Hour[] }) {
  const data = days(hours);
  const max = Math.max(1, ...data.map((d) => d.requests));
  return (
    <div>
      <div class="flex h-24 items-end gap-0.5 border-b border-zinc-200" role="img" aria-label={t("activity.chart")}>
        {data.map((d) => (
          <div
            key={d.date}
            class={`flex-1 rounded-t-sm ${d.errors > 0 ? "bg-amber-400" : "bg-blue-500"}`}
            style={{ height: d.requests > 0 ? `max(${MIN_BAR}px, ${(d.requests / max) * FULL}%)` : "0" }}
            title={t("activity.day", { ...d, date: formatDate(d.date) })}
          />
        ))}
      </div>
      <p class="mt-1 flex justify-between text-xs text-zinc-500">
        <span>{formatDate(data[0]?.date ?? "")}</span>
        <span>{t("activity.total", { count: data.reduce((n, d) => n + d.requests, 0) })}</span>
        <span>{formatDate(data.at(-1)?.date ?? "")}</span>
      </p>
    </div>
  );
}

/** A client's tool, with its key or "this session" under it. */
function Tool({ c }: { c: ClientOut }) {
  return (
    <>
      <span class="block">{describeClient(c)}</span>
      {c.key_name || c.current ? (
        <span class="mt-1 flex flex-wrap gap-1">
          {c.key_name ? <Badge>{t("activity.key", { name: c.key_name })}</Badge> : null}
          {c.current ? <Badge tone="blue">{t("activity.thisSession")}</Badge> : null}
        </span>
      ) : null}
    </>
  );
}

/** The tools that used a credential: what, from where, when. */
function Clients({ clients, onSignOut }: { clients: readonly ClientOut[]; onSignOut?: (c: ClientOut) => void }) {
  return (
    <Table stack={true}>
      <thead class="bg-zinc-50">
        <tr>
          <Th>{t("activity.tool")}</Th>
          <Th>{t("activity.address")}</Th>
          <Th>{t("activity.lastSeen")}</Th>
          {onSignOut ? <Th /> : null}
        </tr>
      </thead>
      <tbody class="divide-y divide-zinc-100">
        {clients.map((c) => (
          <tr key={c.id}>
            <Td>
              <Tool c={c} />
            </Td>
            <Td label={t("activity.address")}>
              <code class="text-xs">{c.ip ?? "—"}</code>
            </Td>
            <Td title={formatInstant(c.last_seen)} label={t("activity.lastSeen")}>
              {formatRelative(c.last_seen)}
            </Td>
            {onSignOut ? (
              <Td align="right" label="">
                {c.session && !c.current ? (
                  <Button size="sm" onClick={() => onSignOut(c)}>
                    {t("activity.signOut")}
                  </Button>
                ) : null}
              </Td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function KeyActivityBody({ space, id }: { space: SpaceOut; id: string }) {
  const r = useResource(`key-activity:${id}`, (signal) =>
    api.get<KeyActivity>(orgApi(space, `/keys/${id}/activity`), undefined, signal),
  );
  return (
    <Async r={r}>
      {(a) => (
        <div class="flex flex-col gap-4">
          <Chart hours={a.hours} />
          {a.clients.length > 0 ? <Clients clients={a.clients} /> : <EmptyState title={t("activity.unused")} />}
        </div>
      )}
    </Async>
  );
}

/** A key's activity (the tools that used it, requests per day) in a dialog. */
export function KeyActivityButton({ space, k }: { space: SpaceOut; k: ApiKey }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        {t("activity.button")}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title={t("activity.title", { name: k.name })}>
        <KeyActivityBody space={space} id={k.id} />
        <DialogActions>
          <Button answer="confirm" variant="primary" onClick={() => setOpen(false)}>
            {t("common.done")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** The tool a key last used, under its "last used" time. */
export function LastClient({ c }: { c: ClientRef | null | undefined }) {
  return c ? <span class="block text-xs text-zinc-500">{describeClient(c)}</span> : null;
}

/** "via Claude Code 2.1" for a change made through a known client. */
export function Via({ c }: { c: ClientRef | null | undefined }) {
  return c ? <span class="text-xs text-zinc-500">{t("activity.via", { client: describeClient(c) })}</span> : null;
}

/** Where I'm signed in and what used my keys, with signing other sessions out. */
export function MySessions() {
  const r = useResource("me:clients", (signal) => api.get<PageClientOut>("/me/clients", undefined, signal));
  const [signOut] = useAction(async (c: ClientOut) => {
    await api.del(`/me/sessions/${c.session}`);
    r.reload();
  });
  return (
    <Async r={r}>
      {(p) =>
        p.data.length > 0 ? (
          <Clients clients={p.data} onSignOut={(c) => signOut(c)} />
        ) : (
          <EmptyState title={t("activity.none")} />
        )
      }
    </Async>
  );
}
